import {NotReadyApiService} from '@app/api/not-ready-api-service';

export interface IndexDataResult {
  label: string;
  chartLabels: string[];
  data: (number | null)[];
  color: string;
}

export interface IndexDataset {
  label: string;
  data: (number | null)[];
  borderColor: string;
  backgroundColor: string;
  borderWidth: number;
}

// Color map for index data lines
const COLOR_MAP: Record<string, string> = {
  Arvi: '#9ACD32',
  Evi: '#32CD32',
  Gndvi: '#006400',
  Mndwi: '#1E90FF',
  Ndmi: '#4169E1',
  Ndvi: '#228B22',
  Ndwi: '#00CED1',
  Orvi: '#2E8B57',
  Osavi: '#3CB371',
};

export class IndexDataManager {
  private loadedDatasets: Map<string, IndexDataset> = new Map();
  private labels: string[] = [];

  constructor(private notReadyApiService: NotReadyApiService) {}

  get allLabels(): string[] {
    return this.labels;
  }


  getColor(indexValue: string): string {
    return COLOR_MAP[indexValue] || '#000000';
  }

  async loadIndexData(seasonId: string, indexValue: string, indexLabel: string): Promise<IndexDataResult | null> {
    try {
      const response = await this.notReadyApiService.GetIndexSeasonReports({
        filters: [
          {
            filterField: 'seasonId',
            filterOperator: 0,
            filterValue: seasonId
          }
        ]
      }, indexValue).toPromise() as any[];

      if (!response || response.length === 0) {
        return null;
      }

      const sorted = [...response].sort((a, b) =>
        new Date(a.dateOnly).getTime() - new Date(b.dateOnly).getTime()
      );

      return {
        label: indexLabel,
        chartLabels: sorted.map(r => r.dateOnly),
        data: sorted.map(r => r.average ?? null),
        color: this.getColor(indexValue)
      };
    } catch (error) {
      console.warn(`Error loading index ${indexValue}:`, error);
      return null;
    }
  }

  storeDataset(label: string, data: IndexDataResult): void {
    const dataset: IndexDataset = {
      label: data.label,
      data: data.data,
      borderColor: data.color,
      backgroundColor: data.color,
      borderWidth: 2
    };
    this.loadedDatasets.set(label, dataset);

    // Store labels from first successful result
    if (this.labels.length === 0 && data.chartLabels.length > 0) {
      this.labels = [...data.chartLabels];
    }
  }

  getCheckedDatasets(checkedLabels: string[]): IndexDataset[] {
    return checkedLabels
      .map(label => this.loadedDatasets.get(label))
      .filter((d): d is IndexDataset => d !== undefined);
  }

  clear(): void {
    this.loadedDatasets.clear();
    this.labels = [];
  }
}

